import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldProps } from '@ngx-formly/bootstrap/form-field';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import Chart from 'chart.js/auto';
import { Checkbox } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';

interface ChartFieldProps extends FormlyFieldProps {
  segments: { label: string; value: number; color?: string; show?: boolean }[];
  segmentsFunction: any;
  evaluatedSegmentsFunction: any;
  totalLabel: string;
  currency: string;
  canHideSections: boolean;
  conditionFields: string[];
}

@Component({
  selector: 'app-donut-chart-field',
  templateUrl: './donut-chart-field.component.html',
  styleUrl: './donut-chart-field.component.scss',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MultiSelectModule,
    CommonModule,
    Checkbox,
    FormsModule,
  ],
})
export class DonutChartFieldComponent
  extends FieldType<FieldTypeConfig<ChartFieldProps>>
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
  chart!: Chart;

  public segments: any[] = [];

  constructor() {
    super();
  }

  public ngOnInit(): void {
    if (this.props.segmentsFunction) {
      this.props.evaluatedSegmentsFunction = eval(this.props.segmentsFunction);
    }
    this.watchOnConditionFields();
    this.computeSegments();
  }

  private computeSegments() {
    let segments;
    if (this.props?.evaluatedSegmentsFunction) {
      segments = this.props?.evaluatedSegmentsFunction?.(this.field);
    } else {
      segments = this.props.segments ?? [];
    }
    if (this.segments?.length) {
      segments.forEach(segment => {
        segment.show = this.segments.find(s => s.label === segment.label)?.show;
      });
    }
    this.segments = segments;
    this.formControl.setValue(this.segments);
  }

  get total() {
    return this.segments.reduce((sum, s) => (s?.show ? sum + s.value : sum), 0);
  }

  ngAfterViewInit() {
    this?.props.segments?.forEach(s => (s.show = true));
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.computeSegments();
    if (changes['to'] && this.chart) {
      this.updateChart();
    }
  }

  initChart() {
    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: this.segments.map(s => s.label),
        datasets: [
          {
            data: this.segments.map(s => s.value),
            backgroundColor: this.segments.map(
              s => s.color || this.fallbackColor()
            ),
            borderWidth: 0,
          },
        ],
      },
      options: {
        cutout: '90%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
    });
  }

  updateCheckbox() {
    setTimeout(() => {
      this.updateChart();
      this.formControl.setValue(this.segments);
    });
  }

  updateChart() {
    const filteredSegments = this.segments.filter(
      s => !this.props.canHideSections || s.show
    );
    this.chart.data.labels = filteredSegments.map(s => s.label);
    this.chart.data.datasets[0].data = filteredSegments.map(s => s.value);
    this.chart.data.datasets[0].backgroundColor = filteredSegments.map(
      s => s.color || this.fallbackColor()
    );

    this.chart.update();
  }

  fallbackColor() {
    return '#cbd5e1'; // neutralny, spójny z UI
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  private watchOnConditionFields(): void {
    this.props.conditionFields?.forEach(field => {
      const relatedField = this.field.form.get(field);
      relatedField.valueChanges.subscribe(() => {
        this.computeSegments();
      });
    });
  }
}
